import { useState } from "react";
import { ArrowUpRight, Factory, Users, Hammer } from "lucide-react";
import { Nav, NavA } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { Pic } from "@/components/site/Pic";
import { TrustBand } from "@/components/site/TrustBand";
import { CareerForm } from "@/components/site/CareerForm";
import { OpenJobs } from "@/components/site/OpenJobs";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/pageMeta";

const PERKS = [
  [Users, "job.p1", "Un'azienda di famiglia dal 1905", "Cinque generazioni nel legno e un team di collaboratori storici con oltre 20 anni di esperienza."],
  [Factory, "job.p2", "Uno stabilimento all'avanguardia", "Linee di produzione a controllo numerico per legno lamellare e X-LAM, il primo impianto del genere nel Sud Italia."],
  [Hammer, "job.p3", "Progetti in tutta Italia e in Europa", "Coperture, case, ponti e stabilimenti balneari: lavori che restano nel tempo."],
];

export default function LavoraConNoi() {
  const t = useT();
  const [job, setJob] = useState("");
  const apply = (title) => { setJob(title); scrollToId("#candidatura"); };
  usePageMeta({
    title: t("job.metaTitle", "Lavora con noi — Emma Industrie"),
    desc: t("job.metaDesc", "Candidati per lavorare in Emma Industrie: produzione di legno lamellare e X-LAM, montaggio, ufficio tecnico, amministrazione e commerciale."),
    path: "/lavora-con-noi",
  });
  return (
    <div className="bg-[#F7F5F0] text-[#121110]" data-testid="careers-page">
      <Nav />
      <main>
        <section className="grain relative bg-[#121110] px-5 pb-20 pt-44 text-[#F7F5F0] sm:px-8 lg:pt-52" data-testid="careers-hero">
          <div className="mx-auto grid max-w-[1500px] gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D98A52]">{t("job.kicker", "Lavora con noi")}</p>
              <h1 className="mt-6 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">{t("job.h1", "Costruisci il futuro del legno con noi.")}</h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-[#CFC8BC] sm:text-lg">{t("job.sub", "Cerchiamo persone appassionate per produzione, montaggio, ufficio tecnico e amministrazione. Inviaci la tua candidatura: la leggiamo tutta.")}</p>
              <NavA to="/lavora-con-noi#candidatura" className="btn-amber mt-10" data-testid="careers-hero-cta">{t("job.cta", "Invia la candidatura")} <ArrowUpRight className="h-4 w-4" /></NavA>
            </div>
            <div className="h-[320px] overflow-hidden lg:col-span-5 lg:h-[460px]">
              <Pic src="/img/emma/stabilimento.jpg" alt={t("job.imgAlt", "Lo stabilimento Emma Industrie a San Cataldo")} className="h-full w-full object-cover" />
            </div>
          </div>
        </section>
        <section className="px-5 py-20 sm:px-8 lg:py-28" data-testid="careers-perks">
          <div className="mx-auto max-w-[1500px]">
            <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("job.whyH", "Perché lavorare in Emma Industrie")}</h2>
            <ul className="mt-12 grid gap-4 md:grid-cols-3">
              {PERKS.map(([Icon, k, h, p]) => (
                <li key={k} className="border border-[#121110]/15 p-8" data-testid={`careers-perk-${k.slice(-2)}`}>
                  <Icon className="h-8 w-8 text-[#C26D38]" strokeWidth={1.4} />
                  <h3 className="mt-8 font-display text-2xl font-semibold tracking-tight">{t(`${k}.h`, h)}</h3>
                  <p className="mt-3 text-base leading-relaxed text-[#4A453F]">{t(`${k}.p`, p)}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
        <OpenJobs onApply={apply} />
        <TrustBand />
        <section id="candidatura" className="px-5 py-20 sm:px-8 lg:py-28" data-testid="careers-form-section">
          <div className="mx-auto grid max-w-[1500px] gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("job.formH", "La tua candidatura")}</h2>
              <p className="mt-4 text-base leading-relaxed text-[#4A453F]">{t("job.formP", "Compila il modulo e allega il CV. Ti risponderemo da info@emmaindustrie.it.")}</p>
            </div>
            <div className="lg:col-span-8"><CareerForm job={job} setJob={setJob} /></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
